import { Fragment, useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Button, EmptyState, IconButton, Spinner, CountBadge } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useBandContext, useBandContextOrNull, useCapability } from "../../app/bandContext.js";
import { useNavigation } from "../../app/hooks/useSession.js";
import { useOpportunities } from "../../app/hooks/useOpportunities.js";
import { useShowInvites, type ShowInviteDto } from "../../app/hooks/useShows.js";
import {
  useOpportunityMoney, moneyByOpportunity, canSeeOppMoney,
} from "../../app/hooks/useOpportunityMoney.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { TasksCard } from "../tasks/TasksCard.js";
import { TaskDrawer } from "../tasks/TaskDrawer.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { MerchRollupCard } from "./MerchRollupCard.js";
import { MyBunkCard } from "../buses/MyBunkCard.js";
import {
  AudienceSection,
  TopCitiesCard,
  RecentReleaseCard,
} from "./DashboardAnalytics.js";
import { DashboardSpotlight } from "./DashboardSpotlight.js";
import { TrendingCard } from "./Trending.js";
import { MemberSpotlight, MerchSpotlight, ProductionSpotlight, TmSpotlight } from "./RoleSpotlights.js";
import { NewDealModal } from "../org/NewDealModal.js";
import { answeredByViewer, feedSortKey, liveFeedItems, liveOpportunities, oppLeftFeed } from "./oppDisplay.js";
import { openRequests, opportunityRow, showInviteRow, showRequestRow } from "./oppCompact.js";
import { OppCompactRow } from "./OppCompactRow.js";
import { ShowRequestDetails } from "../booking/ShowRequestDetails.js";
import { useShowRequests, type ShowRequestView } from "../../app/hooks/useShowRequests.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "../booking/CalendarEventDetails.js";
import { useMe } from "../../app/hooks/useSession.js";
import { MerchBestsellersCard, ProdInputListCard, RecentShowsCard, TmNext3Card } from "./RoleCards.js";
import { UpcomingCard } from "./UpcomingCard.js";
import styles from "./DashboardScreen.module.scss";

type OpportunityDto = components["schemas"]["OpportunityDto"];

/**
 * The band dashboard — a SERVER-DRIVEN layout. The navigation document owns the per-role widget-key
 * lists (`nav.dashboard.{main,rail,below}`, computed by `BuildBandDashboard`); this screen holds NO
 * role→layout matrix, it resolves each key against {@link DASHBOARD_WIDGETS} and fills the three grid
 * slots in the order given. Same contract as the mobile Home stack, and it retires the MVP's
 * browser-side `effectiveRoleKey` switch — including that switch's flash bug, since the layout now
 * arrives WITH the identity document instead of being derived from it.
 *
 * An unknown key renders nothing (fail closed), so a widget the server names before the client ships it
 * simply doesn't appear. Every widget is MONEY-FREE by construction (no prices, guarantees, or
 * settlements) and born-blank safe (null → `—`). No band context ⇒ the org-home empty state. Widgets ride
 * the shell's already-joined `band:{id}`/`user:{id}` groups, so `schedule-changed` /
 * `opportunity-changed` / `sync-completed` repaint them with no extra live-group join.
 */
export function DashboardScreen({
  openTask,
  onOpenTask,
  onNewTask,
  onCloseTask,
}: {
  /** The `?task` search param (a taskId, `"new"`, or undefined) — the routed task drawer/create overlay. */
  openTask?: string;
  onOpenTask?: (taskId: string) => void;
  onNewTask?: () => void;
  onCloseTask?: () => void;
} = {}) {
  const band = useBandContextOrNull();
  if (!band) {
    return (
      <EmptyState
        title="Select a band to get started"
        description="You don't have an active band context yet."
      />
    );
  }
  const openTaskId = onOpenTask ?? (() => {});
  const newTask = onNewTask ?? (() => {});
  const closeTask = onCloseTask ?? (() => {});
  return (
    <DashboardBody
      bandId={band.bandId}
      openTask={openTask}
      onOpenTask={openTaskId}
      onNewTask={newTask}
      onCloseTask={closeTask}
    />
  );
}

/**
 * The widget registry: key → card. The server names the keys and the order; this map is the only thing
 * the client knows about them. A key with no entry renders nothing, which is how a layout can name a
 * card before the client ships it (the role heroes land this way).
 */
const DASHBOARD_WIDGETS: Record<string, (ctx: WidgetContext) => ReactNode> = {
  // Main column — the per-seat heroes and their companions. Which seat gets which is the server's call.
  spotlight: () => <DashboardSpotlight />,
  member_spotlight: () => <MemberSpotlight />,
  tm_spotlight: () => <TmSpotlight />,
  merch_spotlight: () => <MerchSpotlight />,
  prod_spotlight: () => <ProductionSpotlight />,
  recent_shows: () => <RecentShowsCard />,
  tm_next3: () => <TmNext3Card />,
  merch_bestsellers: () => <MerchBestsellersCard />,
  prod_input_list: () => <ProdInputListCard />,
  trending: () => <TrendingCard />,
  recent_release: () => <RecentReleaseCard />,
  // Rail
  opportunities: () => <OpportunitiesBox />,
  merch: () => <MerchRollupCard />,
  upcoming: () => <UpcomingCard />,
  my_bunk: () => <MyBunkCard emptyMode="hide" />,
  todo: (c) => <TasksCard onOpenTask={c.onOpenTask} onNewTask={c.onNewTask} />,
  // Below the grid
  top_cities: () => <TopCitiesCard />,
  audience: () => <AudienceSection />,
};

/** What the registry's factories may need from the screen (the routed task overlay's callbacks). */
interface WidgetContext {
  onOpenTask: (taskId: string) => void;
  onNewTask: () => void;
}

function DashboardBody({
  bandId,
  openTask,
  onOpenTask,
  onNewTask,
  onCloseTask,
}: {
  bandId: string;
  openTask?: string;
  onOpenTask: (taskId: string) => void;
  onNewTask: () => void;
  onCloseTask: () => void;
}) {
  const nav = useNavigation(`band:${bandId}`);
  const layout = nav.data?.dashboard;
  const ctx: WidgetContext = { onOpenTask, onNewTask };

  const slot = (keys: readonly string[] | null | undefined) =>
    (keys ?? []).map((key) => {
      const widget = DASHBOARD_WIDGETS[key];
      return widget ? <Fragment key={key}>{widget(ctx)}</Fragment> : null;
    });

  const below = slot(layout?.below);

  return (
    <div className={styles.page}>
      <div className={styles.dashGrid}>
        <div className={styles.colMain}>{slot(layout?.main)}</div>
        <aside className={styles.dashRail}>{slot(layout?.rail)}</aside>
      </div>
      {below.length > 0 && <div className={styles.dashBelow}>{below}</div>}
      <DashFooter />
      {/* The routed task drawer / create modal — URL-addressable via `?task=:id` (or `?task=new`). */}
      {openTask === "new" ? (
        <TaskCreateModal onClose={onCloseTask} onCreated={onOpenTask} />
      ) : openTask ? (
        <TaskDrawer taskId={openTask} onClose={onCloseTask} />
      ) : null}
    </div>
  );
}

// ---- In-band opportunities -------------------------------------------------------------------------------

/**
 * One row of the merged feed. The card has TWO sources: the band's in-band opportunities, and the
 * show/hold invites addressed to this person from Booking — the reference renders a held date as an
 * opportunity card ("SHOW HOLD" pill, flipping to SHOW on promotion) and lets its invitees answer it like
 * any other. They stay distinct types rather than being flattened into one shape, because they answer on
 * different endpoints and only one of them carries money.
 */
type FeedRow =
  | { kind: "opp"; sort: string; opp: OpportunityDto }
  | { kind: "invite"; sort: string; invite: ShowInviteDto }
  | { kind: "request"; sort: string; request: ShowRequestView };

function OpportunitiesBox() {
  const opps = useOpportunities();
  const invites = useShowInvites();
  const decides = useCapability("booking:decide");
  const requests = useShowRequests({ enabled: decides });
  const [request, setRequest] = useState<string | null>(null);
  // Whose feed this is — needed to find the caller's own answer inside an opportunity's invite list.
  const myPersonId = useMe().data?.person?.personId;
  // ART-328 — nothing is answered from the card any more: each row is the compact card with ONE button, Details
  // (the creator gets Edit). ART-338 — an OPPORTUNITY leaves once it is settled for the viewer (`oppLeftFeed`): an
  // invitee who answered, or the creator once everyone is in; a SHOW invite leaves once answered too (it is on the
  // calendar from then on). Expired items leave too; soonest first.
  const rows: FeedRow[] = [
    ...liveOpportunities(opps.data?.items ?? undefined)
      .filter((opp) => !oppLeftFeed(opp, myPersonId))
      .map((opp): FeedRow => ({ kind: "opp", sort: feedSortKey(opp), opp })),
    ...liveFeedItems(invites.data?.items ?? undefined)
      .filter((invite) => !answeredByViewer(invite, myPersonId))
      .map((invite): FeedRow => ({ kind: "invite", sort: feedSortKey(invite), invite })),
    // ART-327 — a show request waiting on the team, for the people who decide it (Mgmt / the artist AV).
    ...openRequests(requests.data?.requests)
      .map((request): FeedRow => ({ kind: "request", sort: feedSortKey({ date: request.date }), request })),
  ].sort((a, b) => a.sort.localeCompare(b.sort));
  // Money is the separate money-gated sub-resource: ONE call for the whole card (never one per row), and
  // only for a money-team seat — an off-group seat never fetches it, so no money string can reach them
  // (dashboard.txt:247). The map is empty for them and every row lookup renders nothing.
  const { moneyScope } = useBandContext();
  const money = useOpportunityMoney({ enabled: canSeeOppMoney(moneyScope) });
  const moneyFor = moneyByOpportunity(money.data);
  // Who may CREATE an opportunity: Artist/AV + the three roster orgs (PO 2026-09), the same `opportunities:create`
  // token the server gates the create endpoint on — so the affordance and the accepted POST cannot drift.
  const canCreate = useCapability("opportunities:create");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OpportunityDto | null>(null);
  const [details, setDetails] = useState<OpenCalendarEvent | null>(null);
  // Either source failing fails the CARD: a half-rendered feed would silently drop rows this person is
  // expected to answer, which is worse than an honest retry.
  const loading = opps.isLoading || invites.isLoading;
  const failed = opps.isError ? opps.error : invites.isError ? invites.error : null;

  return (
    <section className={styles.box} aria-labelledby="opps-heading">
      <header className={styles.boxHead}>
        <h3 id="opps-heading" className={styles.boxTitle}>
          Opportunities
        </h3>
        <div className={styles.boxHeadMeta}>
          {!loading && !failed && <CountBadge count={rows.length} />}
          {canCreate && (
            <IconButton
              icon={Plus}
              label="New opportunity"
              tone="accent"
              shape="circle"
              size="sm"
              onClick={() => setCreating(true)}
            />
          )}
        </div>
      </header>
      {creating && (
        <NewDealModal
          title="New opportunity"
          submitLabel="Create"
          onClose={() => setCreating(false)}
        />
      )}
      {editing && <NewDealModal opp={editing} title="Edit opportunity" onClose={() => setEditing(null)} />}
      {details && <CalendarEventDetails open={details} onClose={() => setDetails(null)} />}
      {request && <ShowRequestDetails offerId={request} onClose={() => setRequest(null)} />}
      {loading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner />
        </div>
      ) : failed ? (
        <EmptyState
          title="Couldn't load opportunities"
          description={failed instanceof ApiError ? failed.message : "Please try again."}
          action={
            <Button
              onClick={() => {
                void opps.refetch();
                void invites.refetch();
              }}
            >
              Retry
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No opportunities yet."
          description="Tap + to log a show, tour, sync, or anything worth tracking."
        />
      ) : (
        <ul className={`${styles.compactRows} ${styles.oppScroll}`}>
          {rows.map((row) => {
            if (row.kind === "request") {
              const id = row.request.offerId!;
              return (
                <OppCompactRow
                  key={`request:${id}`}
                  {...showRequestRow(row.request)}
                  actionLabel="Details"
                  onAction={() => setRequest(id)}
                />
              );
            }
            if (row.kind === "opp") {
              const m = opportunityRow(row.opp, myPersonId, moneyFor[row.opp.opportunityId!]?.moneyCents);
              const id = row.opp.opportunityId!;
              return (
                <OppCompactRow
                  key={`opp:${id}`}
                  {...m}
                  actionLabel={m.side === "creator" ? "Edit" : "Details"}
                  onAction={() => m.side === "creator"
                    ? setEditing(row.opp)
                    : setDetails({ kind: "opportunity", id, anchor: null })}
                />
              );
            }
            const m = showInviteRow(row.invite);
            const id = row.invite.showId!;
            return (
              <OppCompactRow
                key={`show:${id}`}
                {...m}
                actionLabel="Details"
                onAction={() => setDetails({ kind: "show", id, anchor: null })}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The dashboard footer (`DashFooter`, audit 12.9) — present in both reference specimens and in the legacy
 * tree, and absent here until now.
 *
 * The three links are REAL destinations, not the specimen's `href="#"`: `/support`, `/privacy` and `/terms`
 * are PublicWeb Razor pages served from this same host. They are single-segment paths, so they sit outside
 * the SPA's route table and cannot be shadowed by a band slug — only the `/{artist}/{song}` catch-all can be,
 * which is why those three need no entry in the reserved-slug list. Plain anchors rather than router links
 * for the same reason: the destination is not a SPA route, so a client-side navigation would resolve to
 * nothing.
 */
function DashFooter() {
  return (
    <footer className={styles.dashFooter}>
      <span className={styles.dashFooterCopy}>© {new Date().getFullYear()} CNTRL Global</span>
      <nav className={styles.dashFooterLinks} aria-label="Footer">
        <a href="/support">Support</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
      </nav>
    </footer>
  );
}
